import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from '@environments/environment';
import {DemandaService} from './demanda.service';

export interface PinMapa {
  position: google.maps.LatLngLiteral;
  title: string;
  idTipoObra?: number;
}

interface LocalidadeDemanda {
  latitude: number;
  longitude: number;
  protocolo: string;
  idTipoObra?: number;
}

@Injectable({
  providedIn: 'root'
})
export class MapaDemandasService {
  private readonly SAO_PAULO_LAT = -23.5505;
  private readonly SAO_PAULO_LNG = -46.6333;
  private readonly INITIAL_ZOOM = 8;
  private readonly apiUrl = environment.api.uri + 'demanda/localidade-demandas';

  private readonly centerSource = new BehaviorSubject<google.maps.LatLngLiteral>({
    lat: this.SAO_PAULO_LAT,
    lng: this.SAO_PAULO_LNG
  });
  private readonly zoomSource = new BehaviorSubject<number>(this.INITIAL_ZOOM);
  private readonly pinsSource = new BehaviorSubject<PinMapa[]>([]);
  private readonly heatmapSource = new BehaviorSubject<google.maps.LatLngLiteral[]>([]);

  readonly center$ = this.centerSource.asObservable();
  readonly zoom$ = this.zoomSource.asObservable();
  readonly pins$ = this.pinsSource.asObservable();
  readonly heatmap$ = this.heatmapSource.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly demandaService: DemandaService
  ) {
    this.loadDemandas();
  }

  buildMapOptions(): google.maps.MapOptions {
    return {
      zoomControl: true,
      mapTypeControl: true,
      scaleControl: true,
      streetViewControl: true,
      rotateControl: true,
      maxZoom: 18,
      minZoom: 4,
      fullscreenControl: true,
      mapTypeControlOptions: {position: google.maps.ControlPosition.BOTTOM_LEFT},
      fullscreenControlOptions: {position: google.maps.ControlPosition.BOTTOM_LEFT},
    };
  }

  drawContornoSaoPaulo(googleMap: google.maps.Map): void {
    this.demandaService.getContornoSaoPaulo().subscribe({
      next: (coords) => {
        for (const coord of coords) {
          new google.maps.Polyline({
            path: coord.points,
            strokeColor: '#E41B2E',
            strokeOpacity: 1,
            strokeWeight: 1,
          }).setMap(googleMap);
        }
      },
      error: (err) => console.error('Erro ao obter contornos de SP:', err)
    });
  }

  loadDemandas(): void {
    this.centerSource.next({lat: this.SAO_PAULO_LAT, lng: this.SAO_PAULO_LNG});
    this.zoomSource.next(this.INITIAL_ZOOM);

    this.http.get<LocalidadeDemanda[]>(this.apiUrl).pipe(
      map(localidades => this.processLocalizacoes(localidades))
    ).subscribe({
      next: ({center, zoom, pins, heatmapPoints}) => {
        this.centerSource.next(center);
        this.zoomSource.next(zoom);
        this.pinsSource.next(pins);
        this.heatmapSource.next(heatmapPoints);
      },
      error: (error) => {
        console.error('Erro ao carregar demandas do mapa:', error);
        this.pinsSource.next([]);
        this.heatmapSource.next([]);
      }
    });
  }

  updateMapState(center: google.maps.LatLngLiteral, zoom: number, pins?: PinMapa[]): void {
    this.centerSource.next(center);
    this.zoomSource.next(zoom);
    if (pins !== undefined) {
      this.pinsSource.next(pins);
    }
  }

  setPins(pins: PinMapa[]): void {
    this.pinsSource.next(pins);
  }

  setHeatmapPoints(points: google.maps.LatLngLiteral[]): void {
    this.heatmapSource.next(points);
  }

  private processLocalizacoes(localidades: LocalidadeDemanda[]): {
    center: google.maps.LatLngLiteral;
    zoom: number;
    pins: PinMapa[];
    heatmapPoints: google.maps.LatLngLiteral[];
  } {
    const pins: PinMapa[] = [];
    const heatmapPoints: google.maps.LatLngLiteral[] = [];

    for (const item of localidades) {
      const lat = Number(item.latitude);
      const lng = Number(item.longitude);
      if (!this.isValidCoord(lat, lng)) continue;

      pins.push({position: {lat, lng}, title: item.protocolo, idTipoObra: item.idTipoObra});
      heatmapPoints.push({lat, lng});
    }

    const center = pins.length > 0
      ? this.calculateCenter(pins)
      : {lat: this.SAO_PAULO_LAT, lng: this.SAO_PAULO_LNG};
    const zoom = pins.length > 0
      ? this.calculateZoom(pins)
      : this.INITIAL_ZOOM;

    return {center, zoom, pins, heatmapPoints};
  }

  private isValidCoord(lat: number, lng: number): boolean {
    return !Number.isNaN(lat) && !Number.isNaN(lng) &&
      lat >= -90 && lat <= 90 &&
      lng >= -180 && lng <= 180 &&
      !(lat === 0 && lng === 0);
  }

  private calculateCenter(pins: PinMapa[]): google.maps.LatLngLiteral {
    const lat = pins.reduce((sum, m) => sum + m.position.lat, 0) / pins.length;
    const lng = pins.reduce((sum, m) => sum + m.position.lng, 0) / pins.length;
    return {lat, lng};
  }

  private calculateZoom(pins: PinMapa[]): number {
    const lats = pins.map(m => m.position.lat);
    const lngs = pins.map(m => m.position.lng);
    const latDiff = Math.max(...lats) - Math.min(...lats);
    const lngDiff = Math.max(...lngs) - Math.min(...lngs);

    if (latDiff < 0.001 && lngDiff < 0.001) return 16;

    const zoomFor = (diff: number): number => {
      if (diff > 50) return 4;
      if (diff > 20) return 5;
      if (diff > 10) return 6;
      if (diff > 5) return 7;
      if (diff > 2) return 8;
      if (diff > 1) return 10;
      if (diff > 0.5) return 11;
      return 12;
    };

    return Math.min(zoomFor(latDiff), zoomFor(lngDiff));
  }

}
